import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy, Globe, Loader2, Mic, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useDictation } from "@/lib/useDictation";
import { langLabel } from "@/lib/languages";

const IS_IOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
const IS_MAC = /Mac/i.test(navigator.platform || navigator.userAgent);
const IS_MOBILE = IS_IOS || /Android/i.test(navigator.userAgent);
export const SHORTCUT_LABEL = `${IS_MAC ? "⌘" : "Ctrl"} + Shift + Space`;
const PASTE_STEP = IS_MOBILE ? "Long-press the text box and tap Paste." : `Click the text box and press ${IS_MAC ? "⌘" : "Ctrl"} + V.`;

const TONE_KEY = "mulankan_kb_tone";
const KB_TONES = [{ id: "", label: "As usual" }, { id: "friendly", label: "Friendly" }];

function ToneChips({ p, tone, setTone }) {
  return (
    <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Message tone" data-testid={`${p}-tone-picker`}>
      {KB_TONES.map((t) => (
        <button key={t.id || "usual"} type="button" role="radio" aria-checked={tone === t.id} onClick={() => setTone(t.id)}
          data-testid={`${p}-tone-${t.id || "usual"}`}
          className={`rounded-full px-3 py-1.5 text-[11px] font-semibold border transition-colors ${tone === t.id ? "bg-[var(--accent)] text-black border-[var(--accent)]" : "border-white/10 text-white/55 hover:text-white"}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

const tryCopy = async (t) => { try { await navigator.clipboard.writeText(t); return true; } catch { return false; } };
export const waNumber = (raw) => { const d = (raw || "").replace(/\D/g, ""); return d.length === 10 ? `91${d}` : d; };
export const sendWhatsApp = (text, number = "") =>
  window.open(`https://wa.me/${waNumber(number)}?text=${encodeURIComponent(text)}`, "_blank", "noopener");

const WaIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.3-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.7.7 2.3.8 3.2.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.2-.2-.5-.3Z" /></svg>
);

const Bars = ({ levels, p }) => (
  <div className="flex items-center justify-center gap-[6px] h-20" data-testid={`${p}-waveform`}>
    {levels.slice(-11).map((l, i) => (
      <span key={i} className="w-[9px] rounded-full bg-[var(--ink)] transition-[height] duration-100" style={{ height: `${Math.max(16, l * 100)}%` }} />
    ))}
  </div>
);

function Result({ p, text, setText, copied, onCopy, onAgain }) {
  const [num, setNum] = useState("");
  const bad = num.trim() && waNumber(num).length < 8;
  return (
    <div className="w-full fade-up" data-testid={`${p}-result`}>
      <textarea autoCapitalize="sentences" autoCorrect="on" spellCheck aria-label="Polished text" value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid={`${p}-result-text`}
        className="w-full resize-none rounded-2xl bg-white/[0.05] border border-white/10 p-4 text-[15px] leading-relaxed text-[var(--ink)] outline-none focus:border-[var(--accent)]" />
      <p className={`text-xs mt-2 mb-4 ${copied ? "text-emerald-300" : "text-white/45"}`} data-testid={`${p}-copy-status`}>
        {copied ? `Copied — now paste it. ${PASTE_STEP}` : "Edit if you like, then send or copy."}
      </p>
      <div className="flex items-center gap-2 mb-3">
        <input type="tel" inputMode="tel" autoComplete="tel" enterKeyHint="send" aria-label="WhatsApp number" value={num} onChange={(e) => setNum(e.target.value)} placeholder="WhatsApp number (optional)" data-testid={`${p}-whatsapp-number`}
          className={`flex-1 min-w-0 rounded-full bg-white/[0.05] border px-4 py-2.5 text-sm outline-none ${bad ? "border-rose-400/60" : "border-white/10 focus:border-[#25D366]"}`} />
        {num && <button onClick={() => setNum("")} data-testid={`${p}-whatsapp-number-clear`} className="text-xs text-white/40 hover:text-white/80 transition-colors">Clear</button>}
      </div>
      {bad && <p className="text-xs text-rose-300 -mt-1 mb-3" data-testid={`${p}-whatsapp-number-error`}>Enter a full number with country code, e.g. +91 98765 43210.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <button disabled={!!bad} onClick={() => sendWhatsApp(text, num)} data-testid={`${p}-whatsapp-button`} className="col-span-2 sm:col-span-1 flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-[#062B14] font-bold text-sm py-3 hover:brightness-110 transition-[filter,opacity] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100 whitespace-nowrap"><WaIcon /> {num.trim() ? "Send to number" : "Send in WhatsApp"}</button>
        <button onClick={onCopy} data-testid={`${p}-copy-button`} className="btn-ghost justify-center text-sm whitespace-nowrap"><Copy size={15} /> {copied ? "Copy again" : "Copy"}</button>
        <button onClick={onAgain} data-testid={`${p}-again-button`} className="btn-ghost justify-center text-sm whitespace-nowrap"><RotateCcw size={15} /> Speak again</button>
      </div>
    </div>
  );
}

export function KeyboardPanel({ p = "kb", autoStart = true, onClose, footer, className = "" }) {
  const settings = useRef({});
  const held = useRef(false);
  const [tone, setToneState] = useState(() => localStorage.getItem(TONE_KEY) || "");
  const toneRef = useRef(tone);
  const setTone = (t) => { toneRef.current = t; setToneState(t); localStorage.setItem(TONE_KEY, t); };
  const [lang, setLang] = useState("auto");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const { status, levels, caption, start, stop, cancel } = useDictation({
    source: p === "listen" ? "listen" : "keyboard",
    getOptions: () => (toneRef.current ? { ...settings.current, tone: toneRef.current } : settings.current),
    onResult: async (d) => { setResult(d.text); setCopied(await tryCopy(d.text)); },
  });
  const live = status === "recording" || status === "starting";
  const busy = status === "processing";

  useEffect(() => {
    api.get("/settings").then(({ data }) => { settings.current = data; setLang(data.language); }).catch(() => {}).finally(() => autoStart && start());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const again = useCallback(() => { setResult(null); setCopied(false); start(); }, [start]);
  const copy = async () => { const ok = await tryCopy(result); setCopied(ok); if (!ok) toast.error("Couldn't copy — select the text and copy it."); };

  // Space: hold to talk while the panel is open (outside the text box).
  useEffect(() => {
    const typing = (e) => /INPUT|TEXTAREA/.test(e.target.tagName) || e.target.isContentEditable;
    const down = (e) => {
      if (e.code !== "Space" || e.repeat || typing(e) || busy || e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      if (!live) { held.current = true; setResult(null); setCopied(false); start(); }
    };
    const up = (e) => { if (e.code !== "Space" || !held.current) return; held.current = false; stop(); };
    const esc = (e) => {
      if (e.key === "Escape") { if (live) cancel(); else onClose?.(); }
      // On /listen there is no floating mic, so the panel owns the laptop shortcut.
      if (p !== "kb" && e.code === "Space" && e.shiftKey && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (status === "recording") stop(); else if (!live && !busy) { setResult(null); setCopied(false); start(); }
      }
    };
    window.addEventListener("keydown", down); window.addEventListener("keyup", up); window.addEventListener("keydown", esc);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); window.removeEventListener("keydown", esc); };
  }, [live, busy, status, start, stop, cancel, onClose, p]);

  useEffect(() => {
    const t = (e) => { if (e.detail === "done" && status === "recording") stop(); };
    window.addEventListener("mulankan:kb", t);
    return () => window.removeEventListener("mulankan:kb", t);
  }, [status, stop]);

  return (
    <div className={`w-full sm:max-w-xl mx-auto rounded-t-[28px] sm:rounded-[28px] bg-[#1A1714] border border-white/[0.08] shadow-2xl px-5 pt-4 ${className}`}
      style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }} data-testid={`${p}-panel`}>
      <div className="flex items-center justify-between h-12">
        <button onClick={live ? cancel : onClose} aria-label={live ? "Cancel" : "Close"} data-testid={live ? `${p}-cancel-button` : `${p}-close-button`}
          className="w-11 h-11 rounded-full bg-white/[0.08] flex items-center justify-center hover:bg-white/15 transition-colors"><X size={20} /></button>
        {live && <button onClick={stop} aria-label="Done" data-testid={`${p}-done-button`} className="w-11 h-11 rounded-full bg-[var(--ink)] text-black flex items-center justify-center hover:scale-105 transition-transform"><Check size={22} strokeWidth={3} /></button>}
      </div>
      <div className="min-h-[190px] flex flex-col items-center justify-center text-center py-3">
        {result !== null && !live && !busy ? (
          <Result p={p} text={result} setText={setResult} copied={copied} onCopy={copy} onAgain={again} />
        ) : busy ? (
          <div className="flex flex-col items-center gap-3" data-testid={`${p}-processing`}><Loader2 size={30} className="animate-spin text-[var(--accent)]" /><span className="text-white/60 text-sm">Polishing your words…</span></div>
        ) : live ? (
          <>
            <Bars levels={levels} p={p} />
            <div className="mt-3 text-base text-[var(--ink)]" data-testid={`${p}-status`}>{status === "starting" ? "Starting mic…" : "Listening"}</div>
            <p className="mt-2 max-w-md text-xs text-white/45 italic min-h-[2rem] line-clamp-2" data-testid={`${p}-caption`}>{caption.slice(-160)}</p>
          </>
        ) : (
          <>
            <button onClick={start} data-testid={`${p}-start-button`} aria-label="Tap to speak" className="mic-orb"><Mic size={30} /></button>
            <div className="mt-4 text-white/65 text-sm" data-testid={`${p}-idle-text`}>{IS_MOBILE ? "Tap to speak" : "Tap or hold Space to speak"}</div>
          </>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 pt-2 text-xs text-white/40">
        <span className="flex items-center gap-2 truncate min-w-0" data-testid={`${p}-language`}><Globe size={16} className="shrink-0" /> <span className="truncate">{langLabel(lang)}</span></span>
        <ToneChips p={p} tone={tone} setTone={setTone} />
        {footer || (!IS_MOBILE && <span className="hidden sm:inline" data-testid={`${p}-shortcut-hint`}>{SHORTCUT_LABEL}</span>)}
      </div>
    </div>
  );
}
